import React, { useMemo } from 'react';

import { ImageResizeOptions, Image } from '@wix/wix-ui-tpa';
import { useTranslation } from '@wix/yoshi-flow-editor';

import { messagesModel, selectOneChatSettings } from '@/features';
import {
  senderIsAssistant,
  senderIsOwner,
  senderIsVisitor,
} from '@/utils/sender';
import { useAppSelector } from '@/store';

import {
  useRootProps,
  useWidgetSettings,
} from 'AiAssistantWidget/Widget/contexts';
import {
  CUSTOM_MINIMIZED_BUTTON_ICON,
  MinimizedButtonIcon,
} from 'AiAssistantWidget/Settings/components/DesignMinimizedButton/minimizedButtonIcons';
import settingsParams from 'AiAssistantWidget/settingsParams';

import { AIAvatarIcon, AvatarIcon } from '../svg';

import { classes, st } from './Avatar.st.css';

type AvatarProps = {
  message?: messagesModel.Message;
  memberIconClassname: string;
};

export const Avatar = ({ message, memberIconClassname }: AvatarProps) => {
  const { t } = useTranslation();
  const { avatar: memberAvatar } = useRootProps();
  const widgetSettings = useWidgetSettings();

  const aiAvatar = useAppSelector(selectOneChatSettings.aiAvatar);
  const manualAvatar = useAppSelector(selectOneChatSettings.manualAvatar);

  const isOwnerMessage = senderIsOwner(message?.sender);
  const isAiMessage = senderIsAssistant(message?.sender);
  const isVisitorMessage = senderIsVisitor(message?.sender);

  const assistantAvatar = useMemo(() => {
    const iconKey = widgetSettings.get(
      settingsParams.minimizedButtonIcon,
      'primary',
    );
    const customIconSrc = widgetSettings.get(
      settingsParams.minimizedButtonCustomIcon,
      'primary',
    );

    if (isOwnerMessage) {
      if (manualAvatar) {
        return (
          <Image
            width={28}
            height={28}
            aspectRatio="square"
            src={manualAvatar}
            resize={ImageResizeOptions.cover}
            role="img"
            // TODO: add proper aria-label for manual avatar
            // aria-label={aiAriaLabel}
          />
        );
      }

      if (iconKey === CUSTOM_MINIMIZED_BUTTON_ICON && customIconSrc) {
        return (
          <div role="img">
            <MinimizedButtonIcon
              icon={iconKey}
              customIconSrc={customIconSrc}
              className={st(classes.customIcon)}
            />
          </div>
        );
      }

      // TODO: add proper aria-label for manual avatar
      return <AvatarIcon role="img" className={st(classes.icon)} />;
    }

    if (isAiMessage) {
      const aiAriaLabel = t('app.widget.ariallabel.aiAvatar');

      if (aiAvatar) {
        return (
          <Image
            width={28}
            height={28}
            aspectRatio="square"
            src={aiAvatar}
            resize={ImageResizeOptions.cover}
            role="img"
            aria-label={aiAriaLabel}
          />
        );
      }
      return iconKey ? (
        <div role="img" aria-label={aiAriaLabel}>
          <MinimizedButtonIcon
            icon={iconKey}
            customIconSrc={customIconSrc}
            className={st(classes.customIcon)}
          />
        </div>
      ) : (
        <AIAvatarIcon role="img" aria-label={aiAriaLabel} />
      );
    }

    return (
      // Fallback icon
      <AvatarIcon
        role="img"
        className={st(classes.icon)}
        // aria-label={t('app.widget.ariallabel.userAvatar')}
      />
    );
  }, [widgetSettings, aiAvatar, t, isAiMessage, isOwnerMessage, manualAvatar]);

  if (isVisitorMessage) {
    return memberAvatar ? (
      <img
        className={memberIconClassname}
        src={memberAvatar}
        alt={t('app.widget.chat.userPicAltText')}
      />
    ) : (
      <AvatarIcon
        role="img"
        aria-label={t('app.widget.ariallabel.userAvatar')}
        className={st(classes.icon)}
      />
    );
  }

  return assistantAvatar;
};
